import { Component, signal, OnInit, input, inject, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { AddNewValue } from '../../attributes/add-new-value/add-new-value';
import { AttributeForm } from '../../add-product-component/steps/attributes-step/attribute-form/attribute-form';
import { AttributesTable } from '../../add-product-component/steps/attributes-step/attributes-table/attributes-table';
import { AttributesService } from '../../../../services/productAttributes/productAttributes.service';
import {
  ExtendedAttributeListItem,
  GlobalAttributeItemResponse,
  ProductAttributeItemResponse,
  ReorderItem,
  ExtendedAttribute
} from '../../../../services/productAttributes/productAttributes.model';
import { ConfirmDeleteModal } from '../../add-product-component/steps/attributes-step/confirm-delete-modal/confirm-delete-modal';
import { ImportGlobalModal } from '../../add-product-component/steps/attributes-step/import-global-model/import-global-model';

@Component({
  selector: 'app-product-attributes-component',
  standalone: true,
  imports: [CommonModule, AddNewValue, AttributeForm, AttributesTable, ImportGlobalModal, ConfirmDeleteModal],
  templateUrl: './product-attributes-component.html',
})
export class ProductAttributesComponent implements OnInit {
  private attributesService = inject(AttributesService);

  productId = input.required<number>();

  @ViewChild('valueModal') valueModal!: AddNewValue;

  loading = signal(false);
  attributesLoading = signal(false);
  error = signal<string | null>(null);
  isSaved = signal(false);
  hasPendingChanges = signal(false);

  attributesList = signal<ExtendedAttributeListItem[]>([]);
  globalAttributes = signal<GlobalAttributeItemResponse[]>([]);

  showValueModal = signal(false);
  showImportModal = signal(false);

  currentAttributeIndex = signal<number | null>(null);
  currentValueIndex = signal<number | null>(null);
  editData = signal<{ en: string; ar: string | null } | null>(null);
  editAttrIndex = signal<number | null>(null);
  deleteConfig = signal<{ type: 'attr' | 'value'; attrIdx: number; valIdx?: number; name: string } | null>(null);

  isArabic = () => document.documentElement.dir === 'rtl';

  existingTitleEnList = computed(() => this.attributesList().map((a) => a.attributeKey.titleEn ?? ''));

  currentAttrValues = computed(() => {
    const idx = this.currentAttributeIndex();
    if (idx === null) return [];
    return (this.attributesList()[idx]?.attributeValues ?? []).map((v) => ({ en: v.titleEn ?? '', ar: v.titleAr }));
  });

  editAttrFormData = computed(() => {
    const idx = this.editAttrIndex();
    if (idx === null) return null;
    const attr = this.attributesList()[idx];
    return attr ? { titleEn: attr.attributeKey.titleEn ?? '', titleAr: attr.attributeKey.titleAr, index: idx } : null;
  });

  hasUnsavedChanges = computed(() => this.attributesList().some((attr) => attr.isNewlyAdded));

  canSaveNewChanges = computed(() => {
    const unsaved = this.attributesList().filter((a) => a.isNewlyAdded);
    if (unsaved.length === 0) return this.hasPendingChanges();
    return unsaved.every((attr) => attr.isGlobal || (attr.attributeValues && attr.attributeValues.length > 0));
  });

  ngOnInit(): void {
    this.loadInitialData();
  }

  private loadInitialData(): void {
    this.attributesLoading.set(true);
    forkJoin({
      globals: this.attributesService.getGlobalAttributes(),
      products: this.attributesService.getProductAttributes(this.productId()),
    }).subscribe({
      next: (res) => {
        this.globalAttributes.set(res.globals.list || []);
        this.processList(res.products.list || []);
        this.attributesLoading.set(false);
      },
      error: (err) => this.handleApiError(err),
    });
  }

  private processList(incomingList: ProductAttributeItemResponse[]): void {
    const processed = (incomingList || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item, index) => ({
        ...item,
        order: index + 1,
        isGlobal: item.type?.toLowerCase() === 'global' || (item.isGlobal ?? false),
        isNewlyAdded: false,
      }));
    this.attributesList.set(processed);
    if (processed.length > 0) this.isSaved.set(true);
    this.hasPendingChanges.set(false);
  }

  saveNewChanges(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.error.set(null);

    const unsaved = this.attributesList().filter((a) => a.isNewlyAdded);
    const requests: any = {};

    if (unsaved.length > 0) {
      const dataWithIndices = unsaved.map((attr) => ({
        attr: attr as ExtendedAttribute,
        index: this.attributesList().indexOf(attr)
      }));
      requests.save = this.attributesService.saveProductAttributes(this.productId(), dataWithIndices);
    }

    if (this.hasPendingChanges()) {
      const reorderData: ReorderItem[] = this.attributesList()
        .filter(a => a.id || a.attributeKey.id)
        .map((attr, index) => ({
          productAttributeId: attr.id || attr.attributeKey.id!,
          order: index + 1,
        }));
      requests.reorder = this.attributesService.reorderAttributes(this.productId(), reorderData);
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.loading.set(false);
        this.loadInitialData();
      },
      error: (err) => {
        this.handleApiError(err);
        this.loading.set(false);
      },
    });
  }

  addAttributeKey(key: { titleEn: string; titleAr: string | null }): void {
    const newAttr: any = {
      attributeKey: { titleEn: key.titleEn.trim(), titleAr: key.titleAr?.trim() || null },
      attributeValues: [],
      isGlobal: false,
      isNewlyAdded: true,
    };
    this.attributesList.update((list) => [...list, newAttr]);
  }

  saveEditedAttr(data: { titleEn: string; titleAr: string | null; index: number }): void {
    const attr = this.attributesList()[data.index];
    if (attr.attributeKey.id && !attr.isNewlyAdded) {
      this.attributesService.updateAttributeKey(this.productId(), attr.attributeKey.id, {
          titleEn: data.titleEn,
          titleAr: data.titleAr,
        } as any).subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
    } else {
      this.attributesList.update((list) => {
        const updated = [...list];
        updated[data.index].attributeKey = { ...updated[data.index].attributeKey, titleEn: data.titleEn, titleAr: data.titleAr };
        return updated;
      });
    }
    this.editAttrIndex.set(null);
  }

  handleSaveValue(data: { en: string; ar: string }): void {
    const aIdx = this.currentAttributeIndex();
    const vIdx = this.currentValueIndex();
    if (aIdx === null) return;
    const attr = this.attributesList()[aIdx];
    const val = { titleEn: data.en.trim(), titleAr: data.ar.trim() };

    if (attr.attributeKey.id && !attr.isNewlyAdded) {
      if (vIdx !== null && attr.attributeValues[vIdx].id) {
        this.attributesService.updateAttributeValue(this.productId(), attr.attributeKey.id, attr.attributeValues[vIdx].id!, val)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesService.createAttributeValues(this.productId(), attr.attributeKey.id, [val])
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      }
    } else {
      this.attributesList.update((list) => {
        const u = JSON.parse(JSON.stringify(list));
        if (vIdx !== null) u[aIdx].attributeValues[vIdx] = { ...val, id: 0 };
        else u[aIdx].attributeValues.push({ ...val, id: 0 });
        return u;
      });
    }
    this.closeModal();
  }

  confirmImport(attr: GlobalAttributeItemResponse): void {
    const newGlobal: any = {
      attributeKey: { id: attr.attributeKey.id, titleEn: attr.attributeKey.titleEn, titleAr: attr.attributeKey.titleAr },
      attributeValues: attr.attributeValues ? [...attr.attributeValues] : [],
      isGlobal: true, isNewlyAdded: true,
    };
    this.attributesList.update((list) => [...list, newGlobal]);
    this.showImportModal.set(false);
    this.hasPendingChanges.set(true);
  }

  handleReorder(newList: ExtendedAttributeListItem[]): void {
    this.attributesList.set(newList);
    this.hasPendingChanges.set(true);
  }

  openValueModal(i: number): void {
    if (this.attributesList()[i].isGlobal) return;
    this.currentAttributeIndex.set(i);
    this.currentValueIndex.set(null);
    this.editData.set(null);
    this.showValueModal.set(true);
    setTimeout(() => this.valueModal?.open(), 0);
  }

  editAttributeValue(ai: number, vi: number): void {
    if (this.attributesList()[ai].isGlobal) return;
    const val = this.attributesList()[ai].attributeValues[vi];
    this.currentAttributeIndex.set(ai);
    this.currentValueIndex.set(vi);
    this.editData.set({ en: val.titleEn ?? '', ar: val.titleAr ?? null });
    this.showValueModal.set(true);
    setTimeout(() => this.valueModal?.open(), 0);
  }

  requestDeleteValue(ai: number, vi: number): void {
    const val = this.attributesList()[ai].attributeValues[vi];
    this.deleteConfig.set({ type: 'value', attrIdx: ai, valIdx: vi, name: val.titleEn ?? '' });
  }

  requestDeleteAttr(i: number): void {
    this.deleteConfig.set({ type: 'attr', attrIdx: i, name: this.attributesList()[i].attributeKey.titleEn ?? '' });
  }

  confirmDelete(): void {
    const c = this.deleteConfig();
    if (!c) return;
    const attr = this.attributesList()[c.attrIdx];
    if (c.type === 'attr') {
      if (attr.attributeKey.id && !attr.isNewlyAdded) {
        this.attributesService.deleteAttributeKey(this.productId(), attr.attributeKey.id)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesList.update((l) => l.filter((_, i) => i !== c.attrIdx));
      }
    } else if (c.type === 'value' && c.valIdx !== undefined) {
      const v = attr.attributeValues[c.valIdx];
      if (attr.attributeKey.id && v.id && !attr.isNewlyAdded) {
        this.attributesService.deleteAttributeValue(this.productId(), attr.attributeKey.id, v.id)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesList.update((l) => {
          const u = [...l];
          u[c.attrIdx].attributeValues = u[c.attrIdx].attributeValues.filter((_, i) => i !== c.valIdx);
          return u;
        });
      }
    }
    this.deleteConfig.set(null);
  }

  private handleApiError(err: any): void {
    this.loading.set(false);
    this.attributesLoading.set(false);
    this.error.set(this.isArabic() ? err.error?.messageAr : err.error?.messageEn || 'Error occurred');
  }

  closeModal(): void {
    this.showValueModal.set(false);
    this.currentAttributeIndex.set(null);
    this.currentValueIndex.set(null);
  }
}